<template>
    <div class="rich-html-editor" :class="themeClass">
        <pre :id="id" class="rich-html-editor__content">{{ html }}</pre>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    props: {
        id: {
            type: String,
            default: 'rich-html-editor',
        },
        input: {
            type: String,
            required: true,
        },

        mode: {
            type: String,
            default: 'html',
        },

        theme: {
            type: String,
            default: '',
        },

        resize: {
            type: Boolean,
            default: false,
        },

        autoCompleteWords: {
            type: Array,
            default: () => [],
        },
    },


    data() {
        const $this = this;
        return {
            html: '',
            editor: {},
            customWordCompleter: {
                getCompletions(editor, session, pos, prefix, callback) {
                    const wordList = $this.autoCompleteWords;

                    callback(null, [...wordList.map(function (word) {
                        return {
                            caption: word,
                            value: word,
                            meta: 'data mapping',
                        };
                    }), ...session.$mode.$highlightRules.$keywordList.map(function (word) {
                        return {
                            caption: word,
                            value: word,
                            meta: 'keyword',
                        };
                    })]);
                },
            },
        };
    },

    computed: {
        ...mapState({
            appTheme: state => state.page.appTheme,
        }),

        aceTheme() {
            switch (true) {
                case this.theme === 'code' && this.appTheme === 'dark':
                    return 'ace/theme/dracula';

                default:
                    return 'ace/theme/textmate';
            }
        },

        themeClass() {
            if (!this.theme) return '';

            return `rich-html-editor--${ this.theme }`;
        },
    },

    watch: {
        input(val) {
            this.html = val;
            this.setValue(this.html);
            this.editor.clearSelection();
            this.editor.gotoLine(0, 0);
        },

        async resize() {
            // force ace editor to resize itself
            if (this.resize) {
                this.editor.resize(true);
                this.editor.focus();
            }
        },
    },

    mounted() {
        this.editor = ace.edit(this.id);
        this.editor.session.setMode(`ace/mode/${ this.mode }`);
        this.editor.session.setUseWrapMode(true);
        this.editor.setTheme(this.aceTheme);
        this.editor.setOptions({
            enableBasicAutocompletion: true,
            enableSnippets: true,
            enableLiveAutocompletion: true,
        });

        // comb out unnecessary snippet validation
        const session = this.editor.getSession();

        session.on('changeAnnotation', function () {
            const annotations = session.getAnnotations() || [];
            let i = annotations.length;
            const len = annotations.length;
            while (i--) {
                if (/doctype first\. Expected/.test(annotations[i].text)) {
                    annotations.splice(i, 1);
                }
            }
            if (len > annotations.length) {
                session.setAnnotations(annotations);
            }
        });

        session.on('change', () => {
            this.$emit('change', this.editor.getValue());
        });

        if (this.autoCompleteWords.length) {
            this.editor.completers = [this.customWordCompleter];
        }
    },

    methods: {
        setValue(html) {
            this.editor.setValue(html, 1);
        },

        onSaveOrLeaveEditor() {
            this.$emit('output', this.editor.getValue());
        },

        // inserts text at the cursor position
        insertText(text) {
            // this.editor.session.insert(this.editor.getCursorPosition(), text);
            this.editor.session.replace(this.editor.getSelectionRange(), text);
            this.editor.focus();
        },

        focusEnd() {
            this.editor.focus();
            this.editor.navigateFileEnd();
        },
    },
};
</script>

<style lang="scss">
.rich-html-editor {
    background-color: white;
    padding: 2rem;
    height: 100%;

    &__content {
        font-size: 16px;
        height: 100%;
        width: 100%;
        border: 1px solid rgba(black, 0.1);
    }
}

.rich-html-modal {
    .modal-title {
        font-size: 18px;
    }

    &__loading {
        @extend %center-middle;
    }

    &__error {
        color: $attention-color;
        position: absolute;
        left: 20px;
        font-size: 14px;
    }

    &__button {
        border: 1px solid;
        width: 100px;
        height: 35px;

        &--continue {
            color: var(--form-buttonColor);
            background-color: var(--form-buttonBackgroundColor);
            border: 1px solid var(--form-buttonBorderColor);

            &:hover {
                color: var(--form-buttonHoverColor);
                background-color: var(--form-buttonHoverBackgroundColor);
            }
        }

        &--cancel {
            color: var(--form-altButtonColor);
            background-color: var(--form-altButtonBackgroundColor);
            border: 1px solid var(--form-altButtonBorderColor);

            &:hover {
                color: var(--form-altButtonHoverColor);
                background-color: var(--form-altButtonHoverBackgroundColor);
            }
        }
    }
}
</style>
